import { translateFr as tfr } from "@/i18n";
// Centralized, translation-ready labels for the negotiation workspace.
// Stable semantic keys (negotiationProfile.*) — canonical IDs (sports, metrics,
// tags, statuses) live in backend config and stay language-independent.
// Future i18n migration: move LABELS into locale JSON files and swap `t` for
// the app-wide i18n resolver — component code already only uses keys.

const LABELS = {
  "negotiationProfile.title": "Fiche athlète / Négociation",
  "negotiationProfile.subtitle": "Complétez les informations pour générer un profil professionnel.",
  "negotiationProfile.status.draft": "Brouillon",
  "negotiationProfile.readiness": "Fiche prête à",
  "negotiationProfile.actions.saveDraft": "Enregistrer le brouillon",
  "negotiationProfile.actions.fullscreenPreview": "Prévisualiser plein écran",
  "negotiationProfile.actions.generatePdf": "Générer le PDF",
  "negotiationProfile.actions.advancedEdit": "Édition avancée",
  "negotiationProfile.actions.prevStep": "Étape précédente",
  "negotiationProfile.actions.nextStep": "Étape suivante",
  "negotiationProfile.stepOf": "Étape {a} sur {b}",
  "negotiationProfile.save.saving": "Enregistrement…",
  "negotiationProfile.save.saved": "Brouillon enregistré",
  "negotiationProfile.save.error": "Erreur d'enregistrement — nouvelle tentative à la prochaine modification",
  "negotiationProfile.advanced.warning": "L'édition avancée modifie directement les données utilisées pour le document.",

  "negotiationProfile.steps.identity": "Identité",
  "negotiationProfile.steps.statistics": "Statistiques",
  "negotiationProfile.steps.sport_profile": "Profil sportif",
  "negotiationProfile.steps.contract_market": "Contrat & marché",
  "negotiationProfile.steps.career": "Carrière",
  "negotiationProfile.steps.contact_media": "Contact & média",

  "negotiationProfile.identity.filter.attention": "Informations nécessitant votre attention",
  "negotiationProfile.identity.filter.missing": "Informations manquantes",
  "negotiationProfile.identity.filter.all": "Toutes les informations",
  "negotiationProfile.identity.hint": "Les données confirmées du profil Athlynx sont pré-remplies — inutile de les ressaisir.",
  "negotiationProfile.fieldStatus.athlynx": "Profil Athlynx",
  "negotiationProfile.fieldStatus.modified": "Modifié",
  "negotiationProfile.fieldStatus.missing": "Manquant",

  "negotiationProfile.stats.season": "Saison",
  "negotiationProfile.stats.source": "Source des données",
  "negotiationProfile.stats.selected": "Indicateurs affichés sur la fiche",
  "negotiationProfile.stats.max6": "6 max.",
  "negotiationProfile.stats.hint": "Sélectionnez les 6 indicateurs les plus pertinents pour ce poste, puis renseignez les valeurs.",

  "negotiationProfile.sportProfile.strengths": "Points forts",
  "negotiationProfile.sportProfile.strengthsMax": "Sélectionnés (5 max.)",
  "negotiationProfile.sportProfile.playingStyle": "Style de jeu",
  "negotiationProfile.sportProfile.styleMax": "3 max.",
  "negotiationProfile.sportProfile.environment": "Environnement recherché",
  "negotiationProfile.sportProfile.envMax": "3 max.",
  "negotiationProfile.sportProfile.customStrength": "Ajouter un point fort personnalisé",
  "negotiationProfile.sportProfile.summary": "Résumé du profil",
  "negotiationProfile.sportProfile.summaryProposed": "Résumé proposé",
  "negotiationProfile.sportProfile.propose": "Proposer un résumé",
  "negotiationProfile.sportProfile.aiReformulate": "Reformuler avec l'IA",
  "negotiationProfile.sportProfile.restorePrev": "Restaurer la version précédente",
  "negotiationProfile.sportProfile.approve": "Approuver et inclure dans le PDF",
  "negotiationProfile.sportProfile.approved": "Résumé approuvé — inclus dans le PDF",
  "negotiationProfile.sportProfile.summaryHint": "Généré uniquement à partir des données confirmées et de vos tags — jamais enregistré sans votre validation.",

  "negotiationProfile.contract.status": "Statut contractuel",
  "negotiationProfile.contract.endDate": "Fin de contrat",
  "negotiationProfile.contract.availability": "Disponibilité",
  "negotiationProfile.contract.marketValue": "Valeur de marché estimée",
  "negotiationProfile.contract.salary": "Prétentions salariales (brut)",
  "negotiationProfile.contract.min": "Minimum",
  "negotiationProfile.contract.max": "Maximum",
  "negotiationProfile.contract.period.annual": "Annuel",
  "negotiationProfile.contract.period.monthly": "Mensuel",
  "negotiationProfile.contract.operation": "Type d'opération",
  "negotiationProfile.contract.transferFee": "Indemnité / clause libératoire",
  "negotiationProfile.contract.markets": "Marchés préférés",
  "negotiationProfile.contract.internalNotes": "Observations internes",
  "negotiationProfile.contract.internalOnly": "Interne uniquement — jamais exporté",
  "negotiationProfile.contract.shareCommercial": "Inclure les valeurs commerciales dans la version partageable",
  "negotiationProfile.contract.privacyHint": "Par défaut, valeur de marché, salaire et clause sont exclus de la version partageable.",

  "negotiationProfile.career.title": "Expériences",
  "negotiationProfile.career.addExperience": "Ajouter une expérience",
  "negotiationProfile.career.includePdf": "Inclure dans le PDF",
  "negotiationProfile.career.highlights": "Faits marquants",
  "negotiationProfile.career.addHighlight": "Ajouter un fait marquant",
  "negotiationProfile.career.highlightsMax": "3 à 6 recommandés",

  "negotiationProfile.contact.agent": "Contact agent",
  "negotiationProfile.contact.agentHint": "Renseigné automatiquement depuis votre compte Athlynx.",
  "negotiationProfile.contact.agency": "Agence",
  "negotiationProfile.contact.phone": "Téléphone",
  "negotiationProfile.contact.city": "Ville",
  "negotiationProfile.contact.qr": "Destination du QR code",
  "negotiationProfile.contact.qr.profile": "Profil de l'athlète (Athlynx)",
  "negotiationProfile.contact.qr.custom": "URL personnalisée",
  "negotiationProfile.contact.qr.none": "Désactiver le QR code",
  "negotiationProfile.contact.qr.customUrl": "URL (https://…)",
  "negotiationProfile.contact.qr.warning": "Vérifiez que cette destination est accessible aux destinataires externes.",
  "negotiationProfile.contact.sections": "Sections visibles sur la fiche",

  "negotiationProfile.sections.profile": "Profil athlète",
  "negotiationProfile.sections.contract": "Contrat & marché",
  "negotiationProfile.sections.market_value": "Valeurs commerciales",
  "negotiationProfile.sections.career": "Carrière & faits marquants",
  "negotiationProfile.sections.agent_contact": "Contact agent",
  "negotiationProfile.sections.qr_code": "QR code",

  "negotiationProfile.review.title": "Vérification finale",
  "negotiationProfile.review.audience": "Version du document",
  "negotiationProfile.review.internal": "Version interne (complète)",
  "negotiationProfile.review.external": "Version partageable (sans données sensibles)",
  "negotiationProfile.review.template.standard": "Profil de négociation complet",
  "negotiationProfile.review.template.compact": "Profil essentiel",
  "negotiationProfile.review.missing": "Sections incomplètes",
  "negotiationProfile.review.privacy": "Les notes internes ne sont jamais exportées. Version partageable : valeurs commerciales exclues sauf autorisation explicite.",
  "negotiationProfile.review.forceIncomplete": "Générer quand même (profil simplifié)",
  "negotiationProfile.review.confirm": "Générer le PDF",
  "negotiationProfile.review.cancel": "Annuler",

  "negotiationProfile.preview.title": "Aperçu du document",
  "negotiationProfile.preview.mobileOpen": "Voir l'aperçu",
  "negotiationProfile.preview.zoomIn": "Zoom avant",
  "negotiationProfile.preview.zoomOut": "Zoom arrière",
  "negotiationProfile.preview.fullscreen": "Plein écran",
  "negotiationProfile.preview.close": "Fermer l'aperçu",
  "negotiationProfile.error.load": "Impossible de charger le brouillon.",
  "negotiationProfile.error.retry": "Réessayer",
  "negotiationProfile.breadcrumb.players": "Joueurs",
  "negotiationProfile.breadcrumb.sheet": "Fiche de négociation",
  "negotiationProfile.identity.show": "Afficher",
};

const LABELS_EN = {
  "negotiationProfile.title": "Athlete sheet / Negotiation",
  "negotiationProfile.subtitle": "Fill in the information to generate a professional profile.",
  "negotiationProfile.status.draft": "Draft",
  "negotiationProfile.readiness": "Sheet ready at",
  "negotiationProfile.actions.saveDraft": "Save draft",
  "negotiationProfile.actions.fullscreenPreview": "Preview fullscreen",
  "negotiationProfile.actions.generatePdf": "Generate PDF",
  "negotiationProfile.actions.advancedEdit": "Advanced editing",
  "negotiationProfile.actions.prevStep": "Previous step",
  "negotiationProfile.actions.nextStep": "Next step",
  "negotiationProfile.stepOf": "Step {a} of {b}",
  "negotiationProfile.save.saving": "Saving…",
  "negotiationProfile.save.saved": "Draft saved",
  "negotiationProfile.save.error": "Save error — will retry on next change",
  "negotiationProfile.advanced.warning": "Advanced editing directly modifies the data used for the document.",
  "negotiationProfile.steps.identity": "Identity",
  "negotiationProfile.steps.statistics": "Statistics",
  "negotiationProfile.steps.sport_profile": "Sport profile",
  "negotiationProfile.steps.contract_market": "Contract & market",
  "negotiationProfile.steps.career": "Career",
  "negotiationProfile.steps.contact_media": "Contact & media",
  "negotiationProfile.identity.filter.attention": "Information needing your attention",
  "negotiationProfile.identity.filter.missing": "Missing information",
  "negotiationProfile.identity.filter.all": "All information",
  "negotiationProfile.identity.hint": "Confirmed Athlynx profile data is pre-filled — no need to re-enter it.",
  "negotiationProfile.fieldStatus.athlynx": "Athlynx profile",
  "negotiationProfile.fieldStatus.modified": "Modified",
  "negotiationProfile.fieldStatus.missing": "Missing",
  "negotiationProfile.stats.season": "Season",
  "negotiationProfile.stats.source": "Data source",
  "negotiationProfile.stats.selected": "Indicators shown on the sheet",
  "negotiationProfile.stats.max6": "6 max.",
  "negotiationProfile.stats.hint": "Select the 6 most relevant indicators for this position, then fill in the values.",
  "negotiationProfile.sportProfile.strengths": "Strengths",
  "negotiationProfile.sportProfile.strengthsMax": "Selected (5 max.)",
  "negotiationProfile.sportProfile.playingStyle": "Playing style",
  "negotiationProfile.sportProfile.styleMax": "3 max.",
  "negotiationProfile.sportProfile.environment": "Target environment",
  "negotiationProfile.sportProfile.envMax": "3 max.",
  "negotiationProfile.sportProfile.customStrength": "Add a custom strength",
  "negotiationProfile.sportProfile.summary": "Profile summary",
  "negotiationProfile.sportProfile.summaryProposed": "Suggested summary",
  "negotiationProfile.sportProfile.propose": "Suggest a summary",
  "negotiationProfile.sportProfile.aiReformulate": "Rephrase with AI",
  "negotiationProfile.sportProfile.restorePrev": "Restore previous version",
  "negotiationProfile.sportProfile.approve": "Approve and include in the PDF",
  "negotiationProfile.sportProfile.approved": "Summary approved — included in the PDF",
  "negotiationProfile.sportProfile.summaryHint": "Generated only from confirmed data and your tags — never saved without your validation.",
  "negotiationProfile.contract.status": "Contract status",
  "negotiationProfile.contract.endDate": "Contract end",
  "negotiationProfile.contract.availability": "Availability",
  "negotiationProfile.contract.marketValue": "Estimated market value",
  "negotiationProfile.contract.salary": "Salary expectations (gross)",
  "negotiationProfile.contract.min": "Minimum",
  "negotiationProfile.contract.max": "Maximum",
  "negotiationProfile.contract.period.annual": "Annual",
  "negotiationProfile.contract.period.monthly": "Monthly",
  "negotiationProfile.contract.operation": "Operation type",
  "negotiationProfile.contract.transferFee": "Fee / release clause",
  "negotiationProfile.contract.markets": "Preferred markets",
  "negotiationProfile.contract.internalNotes": "Internal notes",
  "negotiationProfile.contract.internalOnly": "Internal only — never exported",
  "negotiationProfile.contract.shareCommercial": "Include commercial values in the shareable version",
  "negotiationProfile.contract.privacyHint": "By default, market value, salary and clause are excluded from the shareable version.",
  "negotiationProfile.career.title": "Experience",
  "negotiationProfile.career.addExperience": "Add an experience",
  "negotiationProfile.career.includePdf": "Include in the PDF",
  "negotiationProfile.career.highlights": "Highlights",
  "negotiationProfile.career.addHighlight": "Add a highlight",
  "negotiationProfile.career.highlightsMax": "3 to 6 recommended",
  "negotiationProfile.contact.agent": "Agent contact",
  "negotiationProfile.contact.agentHint": "Filled automatically from your Athlynx account.",
  "negotiationProfile.contact.agency": "Agency",
  "negotiationProfile.contact.phone": "Phone",
  "negotiationProfile.contact.city": "City",
  "negotiationProfile.contact.qr": "QR code destination",
  "negotiationProfile.contact.qr.profile": "Athlete profile (Athlynx)",
  "negotiationProfile.contact.qr.custom": "Custom URL",
  "negotiationProfile.contact.qr.none": "Disable QR code",
  "negotiationProfile.contact.qr.customUrl": "URL (https://…)",
  "negotiationProfile.contact.qr.warning": "Make sure this destination is accessible to external recipients.",
  "negotiationProfile.contact.sections": "Sections visible on the sheet",
  "negotiationProfile.sections.profile": "Athlete profile",
  "negotiationProfile.sections.contract": "Contract & market",
  "negotiationProfile.sections.market_value": "Commercial values",
  "negotiationProfile.sections.career": "Career & highlights",
  "negotiationProfile.sections.agent_contact": "Agent contact",
  "negotiationProfile.sections.qr_code": "QR code",
  "negotiationProfile.review.title": "Final review",
  "negotiationProfile.review.audience": "Document version",
  "negotiationProfile.review.internal": "Internal version (full)",
  "negotiationProfile.review.external": "Shareable version (no sensitive data)",
  "negotiationProfile.review.template.standard": "Full negotiation profile",
  "negotiationProfile.review.template.compact": "Essential profile",
  "negotiationProfile.review.missing": "Incomplete sections",
  "negotiationProfile.review.privacy": "Internal notes are never exported. Shareable version: commercial values excluded unless explicitly allowed.",
  "negotiationProfile.review.forceIncomplete": "Generate anyway (simplified profile)",
  "negotiationProfile.review.confirm": "Generate PDF",
  "negotiationProfile.review.cancel": "Cancel",
  "negotiationProfile.preview.title": "Document preview",
  "negotiationProfile.preview.mobileOpen": "View preview",
  "negotiationProfile.preview.zoomIn": "Zoom in",
  "negotiationProfile.preview.zoomOut": "Zoom out",
  "negotiationProfile.preview.fullscreen": "Fullscreen",
  "negotiationProfile.preview.close": "Close preview",
  "negotiationProfile.error.load": "Unable to load the draft.",
  "negotiationProfile.error.retry": "Retry",
  "negotiationProfile.breadcrumb.players": "Players",
  "negotiationProfile.breadcrumb.sheet": "Negotiation sheet",
  "negotiationProfile.identity.show": "Show",
};

const _en = () => { try { return localStorage.getItem("athlynx_lang") === "en"; } catch { return false; } };

export const t = (key, vars) => {
  let s = (_en() ? (LABELS_EN[key] ?? LABELS[key]) : LABELS[key]) ?? key;
  if (vars) Object.entries(vars).forEach(([k, v]) => { s = s.replace(`{${k}}`, v); });
  return s;
};

export const OPERATION_OPTIONS = [
  { id: "transfer", label: tfr("Transfert définitif") },
  { id: "loan", label: tfr("Prêt") },
  { id: "loan_option", label: tfr("Prêt avec option d'achat") },
  { id: "free_agent", label: tfr("Agent libre") },
  { id: "renewal", label: tfr("Prolongation") },
  { id: "sponsorship", label: tfr("Négociation sponsoring") },
  { id: "other", label: tfr("Autre") },
];

export const CONTRACT_STATUS_OPTIONS = [
  "Sous contrat", "Fin de contrat proche", "Libre", "En prêt", "Contrat amateur", "Sans contrat pro",
].map((s) => tfr(s));

export const MARKET_SUGGESTIONS = [
  "France", "Ligue 1", "Ligue 2", "Belgique", "Suisse", "Allemagne", "Espagne",
  "Italie", "Portugal", "Angleterre", "Pays-Bas", "Top 5 européen", "MLS", "Moyen-Orient",
].map((s) => tfr(s));

export const SEASON_OPTIONS = [tfr("Saison 2024/2025"), tfr("Saison 2025/2026"), tfr("Saison 2026/2027")];

export const SOURCE_OPTIONS = [
  { id: "athlynx", label: "Athlynx" },
  { id: "manual", label: tfr("Saisie manuelle") },
  { id: "statsbomb", label: "StatsBomb" },
  { id: "wyscout", label: "Wyscout" },
  { id: "csv", label: "Import CSV" },
  { id: "excel", label: "Import Excel" },
];

export const HIGHLIGHT_SUGGESTIONS = [
  "Sélection nationale", "Promotion", "Trophée", "Record personnel",
  "Premier contrat professionnel", "Capitanat", "Tournoi majeur",
].map((s) => tfr(s));
